import maplibregl from 'maplibre-gl';
import BaseProduct from './BaseProduct';
import LayerManager from './LayerManager';
import { ProductId, SnowStation } from '../types';
import store, { getLayerOpacity } from '../state';
import { getTileUrl, appendAuthToken } from '../config';
import { snowDepthModal } from '../ui/panels/SnowDepthModal';

export class SnowDepthLayer extends BaseProduct {
  public readonly id: ProductId = 'snow_depth';
  private hoverPopup: maplibregl.Popup | null = null;
  private unsubscribeStore: (() => void) | null = null;
  private interactionsBound: boolean = false;
  private currentUnits: 'imperial' | 'metric' = 'imperial';
  private currentMinDepth: number = 0;
  private currentMinElev: number = 0;
  private showStations: boolean = true;
  private currentModel: string = 'snodas';

  constructor(map: maplibregl.Map, layerManager: LayerManager) {
    super(map, layerManager);

    this.unsubscribeStore = store.subscribe((state) => {
      const unitsChanged = this.currentUnits !== state.snowDepthUnits;
      const minDepthChanged = this.currentMinDepth !== state.snowDepthMinInches;
      const minElevChanged = this.currentMinElev !== state.snowDepthMinElevationFt;
      const showStationsChanged = this.showStations !== state.snowDepthShowStations;
      const modelChanged = this.currentModel !== state.snowDepthModel;

      if (unitsChanged || minDepthChanged || minElevChanged || showStationsChanged) {
        this.currentUnits = state.snowDepthUnits || 'imperial';
        this.currentMinDepth = state.snowDepthMinInches || 0;
        this.currentMinElev = state.snowDepthMinElevationFt || 0;
        this.showStations = state.snowDepthShowStations !== false;

        if (this.active) {
          this.updateStationStyles();
        }
      }

      if (modelChanged) {
        this.currentModel = state.snowDepthModel || 'snodas';
        if (this.active) {
          this.refreshRaster();
        }
      }

      if (state.snowDepthEnabled !== this.active) {
        if (state.snowDepthEnabled) {
          this.activate();
        } else {
          this.deactivate();
        }
      }
    });
  }

  protected async onActivate(): Promise<void> {
    this.refreshRaster();
    await this.initStationSourceAndLayers();
    this.setVisibility(true);
  }

  protected onDeactivate(): void {
    this.layerManager.removeBufferedRasterLayer(this.id);
    this.layerManager.removeLayer(this.id);
    this.setVisibility(false);
    if (this.hoverPopup) {
      this.hoverPopup.remove();
      this.hoverPopup = null;
    }
  }

  public destroy(): void {
    this.onDeactivate();
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    if (this.active) {
      this.refreshRaster();
    }
  }

  public refresh(): void {
    this.refreshRaster();
  }

  private refreshRaster(): void {
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const opacity = getLayerOpacity(this.id, 1.0);
    const todayStr = new Date().toISOString().split('T')[0];
    let dateStr = state.currentTimeStep ? state.currentTimeStep.toISOString().split('T')[0] : todayStr;
    
    // Observed Snow Depth only exists up to today; if timeline is in future, fallback to unversioned latest
    if (dateStr > todayStr) {
      dateStr = '';
    }

    const model = state.snowDepthModel || 'snodas';

    const availMap = manifest?.snow_depth?.available_by_model?.[model] || (manifest?.snow_depth as any)?.available?.[model];
    if (availMap && typeof availMap === 'object') {
      const dates = Object.keys(availMap).sort();
      if (dates.length > 0 && dateStr && !dates.includes(dateStr)) {
        const pastDates = dates.filter(d => d <= dateStr);
        dateStr = pastDates.length > 0 ? pastDates[pastDates.length - 1] : dates[dates.length - 1];
      }
    }

    const tiles = [
      getTileUrl(manifest, this.id, model, dateStr)
    ];

    this.layerManager.updateBufferedRasterLayer(this.id, tiles, opacity, undefined, 0, 6);
  }

  private async initStationSourceAndLayers(): Promise<void> {
    if (!this.map.getSource('snow-stations-source')) {
      const manifest = store.getState().manifest;
      const apiServer = manifest?.api_server || '';
      const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');

      let cleanPath = `${tileRoot}/observed/snow_depth/snow_stations.geojson`.replace(/\/+/g, '/');
      if (cleanPath.startsWith('/')) {
        cleanPath = cleanPath.substring(1);
      }
      const rawUrl = apiServer ? `${apiServer}/${cleanPath}` : cleanPath;
      const url = appendAuthToken(rawUrl);

      this.map.addSource('snow-stations-source', {
        type: 'geojson',
        data: url,
        cluster: false,
        tolerance: 0,
        buffer: 0
      });
    }

    if (!this.map.getLayer('snow-station-circles')) {
      const filterExpr = this.getFilterExpression();
      const circleColorExpr = this.getStationColorExpression();

      this.map.addLayer({
        id: 'snow-station-circles',
        type: 'circle',
        source: 'snow-stations-source',
        filter: filterExpr,
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            4, 4.0,
            8, 7.5,
            12, 11.0
          ],
          'circle-color': circleColorExpr as any,
          'circle-stroke-color': '#0f172a',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.95,
          'circle-stroke-opacity': 0.9
        }
      });
    }

    if (!this.map.getLayer('snow-station-labels')) {
      const filterExpr = this.getFilterExpression();
      this.map.addLayer({
        id: 'snow-station-labels',
        type: 'symbol',
        source: 'snow-stations-source',
        filter: filterExpr,
        minzoom: 8.5,
        layout: {
          'text-field': [
            'case',
            ['!=', ['get', 'snow_depth_in'], null],
            ['concat', ['to-string', ['round', ['get', 'snow_depth_in']]], '"'],
            ''
          ],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 10,
          'text-offset': [0, -1.2],
          'text-anchor': 'bottom',
          'text-allow-overlap': false
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#0f172a',
          'text-halo-width': 1.5
        }
      });
    }

    if (!this.interactionsBound) {
      this.setupInteractions();
      this.interactionsBound = true;
    }
  }

  private getFilterExpression(): any {
    const filters: any[] = ['all'];
    if (this.currentMinDepth > 0) {
      filters.push(['>=', ['coalesce', ['get', 'snow_depth_in'], 0], this.currentMinDepth]);
    }
    if (this.currentMinElev > 0) {
      filters.push(['>=', ['coalesce', ['get', 'elevation_ft'], 0], this.currentMinElev]);
    }
    return filters.length > 1 ? filters : ['has', 'snow_depth_in'];
  }

  private getStationColorExpression(): any {
    return [
      'step',
      ['coalesce', ['get', 'snow_depth_in'], 0],
      '#64748b',   // < 0.5" (Slate Gray)
      0.5, '#38bdf8', // 0.5 - 6" (Cyan)
      6, '#1d4ed8',   // 6 - 24" (Medium Royal Blue)
      24, '#4338ca',  // 24 - 60" (Deep Cobalt Indigo)
      60, '#7e22ce',  // 60 - 120" (Vivid Purple)
      120, '#f472b6'  // > 120" (Radiant Pink / White)
    ];
  }

  private updateStationStyles(): void {
    const filterExpr = this.getFilterExpression();
    const isMetric = this.currentUnits === 'metric';

    if (this.map.getLayer('snow-station-circles')) {
      this.map.setFilter('snow-station-circles', filterExpr);
      this.map.setPaintProperty('snow-station-circles', 'circle-color', this.getStationColorExpression());
    }

    if (this.map.getLayer('snow-station-labels')) {
      this.map.setFilter('snow-station-labels', filterExpr);
      const labelField = isMetric
        ? [
            'case',
            ['!=', ['get', 'snow_depth_in'], null],
            ['concat', ['to-string', ['round', ['*', ['get', 'snow_depth_in'], 2.54]]], ' cm'],
            ''
          ]
        : [
            'case',
            ['!=', ['get', 'snow_depth_in'], null],
            ['concat', ['to-string', ['round', ['get', 'snow_depth_in']]], '"'],
            ''
          ];
      this.map.setLayoutProperty('snow-station-labels', 'text-field', labelField as any);
    }

    this.setVisibility(this.active);
  }

  private setVisibility(visible: boolean): void {
    const stationVis = (visible && this.showStations) ? 'visible' : 'none';
    if (this.map.getLayer('snow-station-circles')) {
      this.map.setLayoutProperty('snow-station-circles', 'visibility', stationVis);
    }
    if (this.map.getLayer('snow-station-labels')) {
      this.map.setLayoutProperty('snow-station-labels', 'visibility', stationVis);
    }
  }

  private setupInteractions(): void {
    this.hoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 12,
      className: 'snow-station-hover-popup'
    });

    const onEnter = (e: maplibregl.MapLayerMouseEvent) => {
      this.map.getCanvas().style.cursor = 'pointer';
      if (!e.features || !e.features.length) return;

      const feat = e.features[0];
      const props: any = feat.properties;
      const coords = (feat.geometry as any).coordinates.slice();
      const isMetric = store.getState().snowDepthUnits === 'metric';

      const name = props.name || 'Snow Observation Station';
      const network = props.network || 'SNOTEL';
      const stateProv = props.state_prov || '';
      const depthIn = props.snow_depth_in !== undefined && props.snow_depth_in !== null ? Number(props.snow_depth_in) : null;
      const sweIn = props.swe_in !== undefined && props.swe_in !== null ? Number(props.swe_in) : null;
      const delta24h = props.delta_24h_in !== undefined && props.delta_24h_in !== null ? Number(props.delta_24h_in) : null;
      const tempF = props.air_temp_f !== undefined && props.air_temp_f !== null ? Number(props.air_temp_f) : null;
      const elevFt = props.elevation_ft !== undefined && props.elevation_ft !== null ? Number(props.elevation_ft) : null;

      const depthDisplay = depthIn !== null
        ? (isMetric ? `${Math.round(depthIn * 2.54)} cm` : `${depthIn.toFixed(0)}"`)
        : 'N/A';

      const sweDisplay = sweIn !== null
        ? (isMetric ? `${(sweIn * 2.54).toFixed(1)} cm` : `${sweIn.toFixed(1)}"`)
        : 'N/A';

      const deltaDisplay = delta24h !== null
        ? (delta24h > 0 ? `+${isMetric ? Math.round(delta24h * 2.54) + ' cm' : delta24h + '"'}` : `${isMetric ? Math.round(delta24h * 2.54) + ' cm' : delta24h + '"'}`)
        : null;

      const tempDisplay = tempF !== null
        ? (isMetric ? `${Math.round((tempF - 32) * 5 / 9)}°C` : `${Math.round(tempF)}°F`)
        : 'N/A';

      const elevDisplay = elevFt !== null
        ? (isMetric ? `${Math.round(elevFt * 0.3048).toLocaleString()} m` : `${Math.round(elevFt).toLocaleString()} ft`)
        : 'N/A';

      const html = `
        <div style="background: rgba(15, 23, 42, 0.95); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 8px; padding: 10px 14px; font-family: -apple-system, sans-serif; color: #fff; box-shadow: 0 10px 25px rgba(0,0,0,0.5); min-width: 180px;">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 6px; margin-bottom: 4px;">
            <span style="font-weight: 700; font-size: 13px; color: #fff;">${name}</span>
            <span style="background: #2563eb; font-size: 9px; font-weight: 700; padding: 1px 5px; border-radius: 3px; text-transform: uppercase;">${network}</span>
          </div>
          <div style="font-size: 11px; color: #94a3b8; margin-bottom: 8px;">${stateProv} • Elev ${elevDisplay}</div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; border-top: 1px solid rgba(255,255,255,0.1); padding-top: 6px;">
            <div>
              <div style="font-size: 10px; color: #38bdf8; text-transform: uppercase; font-weight: 600;">Snow Depth</div>
              <div style="font-size: 15px; font-weight: 800; color: #fff;">${depthDisplay} ${deltaDisplay ? `<span style="font-size: 10px; font-weight: 600; color: ${deltaDisplay.startsWith('+') ? '#4ade80' : '#fb923c'};">(${deltaDisplay})</span>` : ''}</div>
            </div>
            <div>
              <div style="font-size: 10px; color: #818cf8; text-transform: uppercase; font-weight: 600;">SWE</div>
              <div style="font-size: 15px; font-weight: 800; color: #fff;">${sweDisplay}</div>
            </div>
          </div>
          <div style="font-size: 11px; color: #f472b6; margin-top: 6px; font-weight: 600;">Air Temp: ${tempDisplay}</div>
        </div>
      `;

      if (this.hoverPopup) {
        this.hoverPopup.setLngLat(coords).setHTML(html).addTo(this.map);
      }
    };

    const onLeave = () => {
      this.map.getCanvas().style.cursor = '';
      if (this.hoverPopup) {
        this.hoverPopup.remove();
      }
    };

    const onClick = (e: maplibregl.MapLayerMouseEvent) => {
      if (!e.features || !e.features.length) return;
      const feat = e.features[0];
      const props: any = feat.properties;

      let historyData: any = undefined;
      if (props.history) {
        try {
          historyData = typeof props.history === 'string' ? JSON.parse(props.history) : props.history;
        } catch {
          historyData = undefined;
        }
      }

      const station: SnowStation = {
        id: props.id || '',
        name: props.name || 'Snow Observation Station',
        network: props.network || 'SNOTEL',
        state_prov: props.state_prov || '',
        lat: Number(props.lat || (feat.geometry as any).coordinates[1]),
        lon: Number(props.lon || (feat.geometry as any).coordinates[0]),
        elevation_ft: Number(props.elevation_ft || 0),
        snow_depth_in: props.snow_depth_in !== undefined && props.snow_depth_in !== null ? Number(props.snow_depth_in) : null,
        swe_in: props.swe_in !== undefined && props.swe_in !== null ? Number(props.swe_in) : null,
        delta_24h_in: props.delta_24h_in !== undefined && props.delta_24h_in !== null ? Number(props.delta_24h_in) : null,
        delta_7d_in: props.delta_7d_in !== undefined && props.delta_7d_in !== null ? Number(props.delta_7d_in) : null,
        swe_pct_normal: props.swe_pct_normal !== undefined && props.swe_pct_normal !== null ? Number(props.swe_pct_normal) : null,
        air_temp_f: props.air_temp_f !== undefined && props.air_temp_f !== null ? Number(props.air_temp_f) : null,
        time: props.time || new Date().toISOString(),
        history: historyData
      };

      if (this.hoverPopup) {
        this.hoverPopup.remove();
      }
      store.update({ snowDepthSelectedStation: station });
      snowDepthModal.open(station);
    };

    for (const lid of ['snow-station-circles', 'snow-station-labels']) {
      this.map.on('mouseenter', lid, onEnter);
      this.map.on('mouseleave', lid, onLeave);
      this.map.on('click', lid, onClick);
    }
  }
}

export default SnowDepthLayer;
